import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Building2, Loader2 } from 'lucide-react';
import { apiRequest } from '@/utils/api-request';
import { showToast } from '@/components/ui/Toast';

interface InfrastructureData {
    library: number;
    playground: number;
    cafeteria: number;
    cctv: number;
    fire_escape: number;
    laboratory: number;
    smart_classrooms: number;
    medical: number;
    computers_facility: number;
    pre_school: number;
    transport: number;
    books: string;
    transport_ownership: string;
    total_hostel_capacity: string;
    amenities_notes: string;
}

const Infrastructure: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [data, setData] = useState<InfrastructureData>({
        library: 0,
        playground: 0,
        cafeteria: 0,
        cctv: 0,
        fire_escape: 0,
        laboratory: 0,
        smart_classrooms: 0,
        medical: 0,
        computers_facility: 0,
        pre_school: 0,
        transport: 0,
        books: '',
        transport_ownership: '',
        total_hostel_capacity: '',
        amenities_notes: '',
    });

    useEffect(() => {
        fetchInfrastructureData();
    }, [id]);

    const fetchInfrastructureData = async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await apiRequest(`/infrastructure/${id}`);
            if (response.success && response.data) {
                setData(response.data);
            } else {
                setError(response.message || 'Failed to load infrastructure information');
            }
        } catch (err: any) {
            console.error('Error fetching infrastructure data:', err);
            setError('Failed to load infrastructure information');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        try {
            setSaving(true);
            setError(null);

            const response = await apiRequest(`/infrastructure/${id}`, {
                method: 'PUT',
                body: data,
            });

            if (response.success) {
                showToast('Infrastructure information updated successfully!', 'success');
            } else {
                throw new Error(response.message || 'Failed to update');
            }
        } catch (err: any) {
            console.error('Error saving infrastructure data:', err);
            const errorMessage = err.message || 'Failed to save infrastructure information';
            setError(errorMessage);
            showToast(errorMessage, 'error');
        } finally {
            setSaving(false);
        }
    };

    const handleToggle = (field: keyof InfrastructureData) => {
        setData(prev => ({
            ...prev,
            [field]: prev[field] === 1 ? 0 : 1,
        }));
    };

    const handleInputChange = (field: keyof InfrastructureData, value: string) => {
        setData(prev => ({
            ...prev,
            [field]: value,
        }));
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-full">
                <Loader2 className="h-8 w-8 text-brandColor animate-spin" />
            </div>
        );
    }

    if (error && !data) {
        return (
            <div className="p-8">
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                    <p className="text-red-800 dark:text-red-200">{error}</p>
                </div>
            </div>
        );
    }

    const toggleFields = [
        { key: 'library' as keyof InfrastructureData, label: 'Library' },
        { key: 'playground' as keyof InfrastructureData, label: 'Playground' },
        { key: 'cafeteria' as keyof InfrastructureData, label: 'Cafeteria' },
        { key: 'cctv' as keyof InfrastructureData, label: 'CCTV' },
        { key: 'fire_escape' as keyof InfrastructureData, label: 'Fire Escape' },
        { key: 'laboratory' as keyof InfrastructureData, label: 'Laboratory' },
        { key: 'smart_classrooms' as keyof InfrastructureData, label: 'Smart Class Rooms' },
        { key: 'medical' as keyof InfrastructureData, label: 'Medical' },
        { key: 'computers_facility' as keyof InfrastructureData, label: 'Computer Facility' },
        { key: 'pre_school' as keyof InfrastructureData, label: 'Pre School' },
        { key: 'transport' as keyof InfrastructureData, label: 'Transport Facility' },
    ];

    return (
        <div>
            {/* Header - Fixed */}
            <div className="sticky top-0 z-10 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-8 py-4">
                <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                        <div className="flex-shrink-0">
                            <div className="p-2 bg-brandColor rounded-lg text-white">
                                <Building2 className="w-6 h-6" />
                            </div>
                        </div>
                        <div className="flex-1 min-w-0">
                            <h1 className="text-lg font-semibold text-gray-900 dark:text-white">Infrastructure</h1>
                            <p className="text-xs text-gray-500 dark:text-gray-400">Manage school infrastructure and facilities</p>
                        </div>
                    </div>
                    <div className="flex-shrink-0">
                        <button
                            type="submit"
                            form="infrastructure-form"
                            disabled={saving}
                            className="inline-flex items-center px-4 py-2 bg-brandColor hover:bg-brand-hover text-white font-medium rounded-lg shadow-sm hover:shadow-md transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brandColor text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                            <span>{saving ? 'Saving...' : 'Save Changes'}</span>
                        </button>
                    </div>
                </div>
            </div>

            {error && (
                <div className="mx-8 mt-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                    <p className="text-red-800 dark:text-red-200">{error}</p>
                </div>
            )}

            {/* Form */}
            <form id="infrastructure-form" onSubmit={handleSubmit} className="p-8 space-y-6">
                {/* Facilities Toggles */}
                <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                    <div className="bg-brandColor px-6 py-3">
                        <h2 className="text-lg font-semibold text-white">Facilities</h2>
                    </div>
                    <div className="p-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {toggleFields.map((field) => (
                                <div
                                    key={field.key}
                                    className="flex items-center justify-between p-4 border border-gray-200 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                                >
                                    <label htmlFor={field.key} className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                        {field.label}
                                    </label>
                                    <button
                                        type="button"
                                        id={field.key}
                                        onClick={() => handleToggle(field.key)}
                                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-brandColor focus:ring-offset-2 ${
                                            data[field.key] === 1 ? 'bg-brandColor' : 'bg-gray-200 dark:bg-gray-600'
                                        }`}
                                    >
                                        <span
                                            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                                data[field.key] === 1 ? 'translate-x-6' : 'translate-x-1'
                                            }`}
                                        />
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Additional Details */}
                <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                    <div className="bg-brandColor px-6 py-3">
                        <h2 className="text-lg font-semibold text-white">Additional Details</h2>
                    </div>
                    <div className="p-6 space-y-6">
                        {/* Books */}
                        <div>
                            <label htmlFor="books" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                Books
                            </label>
                            <input
                                type="text"
                                id="books"
                                value={data.books}
                                onChange={(e) => handleInputChange('books', e.target.value)}
                                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-brandColor focus:border-transparent dark:bg-gray-700 dark:text-white"
                                placeholder="e.g., 10000+ books"
                            />
                        </div>

                        {/* Transport Ownership */}
                        <div>
                            <label htmlFor="transport_ownership" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                Transport Ownership
                            </label>
                            <select
                                id="transport_ownership"
                                value={data.transport_ownership}
                                onChange={(e) => handleInputChange('transport_ownership', e.target.value)}
                                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-brandColor focus:border-transparent dark:bg-gray-700 dark:text-white"
                            >
                                <option value="">Select...</option>
                                <option value="Owned">Owned</option>
                                <option value="Rented">Rented</option>
                                <option value="Not Available">Not Available</option>
                            </select>
                        </div>

                        {/* Hostel Capacity */}
                        <div>
                            <label htmlFor="total_hostel_capacity" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                Hostel Capacity
                            </label>
                            <input
                                type="text"
                                id="total_hostel_capacity"
                                value={data.total_hostel_capacity}
                                onChange={(e) => handleInputChange('total_hostel_capacity', e.target.value)}
                                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-brandColor focus:border-transparent dark:bg-gray-700 dark:text-white"
                                placeholder="e.g., 100 students"
                            />
                        </div>

                        {/* Amenities Notes */}
                        <div>
                            <label htmlFor="amenities_notes" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                Amenities Notes
                            </label>
                            <textarea
                                id="amenities_notes"
                                value={data.amenities_notes}
                                onChange={(e) => handleInputChange('amenities_notes', e.target.value)}
                                rows={4}
                                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-brandColor focus:border-transparent dark:bg-gray-700 dark:text-white resize-none"
                                placeholder="Additional notes about amenities and facilities..."
                            />
                        </div>
                    </div>
                </div>
            </form>
        </div>
    );
};

export default Infrastructure;
